// MCP Security — licensing-v2 route gate.
//
// Wraps every page under /v2/mcp/* with the same "Feature not licensed"
// panel the API Security module already uses (api_security/AsecPage.js
// FeatureNotLicensed). When the current org has no mcp_security SKU,
// `useEffectiveFeature(featureKey)` resolves to `false` and we render
// the lock card instead of the real page. While the effective-features
// map is still loading (undefined) we show a lightweight spinner so a
// legitimately-licensed operator doesn't see a flash of the upsell.
//
// Direct URL access to e.g. /v2/mcp/overview by an unlicensed user hits
// this gate on first paint; the sidebar nav is also hidden by Normal.js's
// MCP special-case, so unlicensed users never navigate here through the UI.

import React from "react";
import { Box, Stack, Typography, Button, CircularProgress } from "@mui/material";
import { Lock, Mail } from "lucide-react";
import colors from "../../../../../../utils/colors";
import {
  useEffectiveFeature,
  useEffectiveFeaturesState,
} from "../../../../../../contexts/user/EffectiveFeaturesContext";
import useAuth from "../../../../../../hooks/useAuth";

const FEATURE_DISPLAY_NAMES = {
  "mcp.endpoints": "MCP Endpoints",
  "mcp.insights":  "MCP Insights",
  "mcp.policy":    "MCP Policy",
  "mcp.reports":   "MCP Reports",
  "mcp.alerts":    "MCP Alerts",
};

function FeatureNotLicensed({ featureKey }) {
  const displayName = FEATURE_DISPLAY_NAMES[featureKey] || "This MCP Security capability";
  return (
    <Box sx={{ p: 6 }}>
      <Box
        sx={{
          borderRadius: "12px",
          border: `solid 1px ${colors.neutral[200]}`,
          backgroundColor: "white",
          p: 6,
        }}
      >
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ minHeight: "360px" }}>
          <Box
            sx={{
              width: 72, height: 72,
              borderRadius: "50%",
              backgroundColor: colors.primary["alpha"],
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Lock size={32} color={colors.primary[300]} strokeWidth={2} />
          </Box>
          <Stack spacing={1.5} alignItems="center">
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: colors.neutral[1000], textAlign: "center" }}>
              {displayName} isn&apos;t included in your plan
            </Typography>
            <Typography
              sx={{
                fontSize: 14, color: colors.neutral[700], textAlign: "center",
                maxWidth: 500, lineHeight: "22px",
              }}
            >
              MCP Security adds runtime protection for Model Context Protocol
              endpoints &mdash; schema &amp; tool-use validation, drift detection,
              session integrity, prompt-injection scanning, and reports. Contact
              your account team to enable MCP Security on this organisation.
            </Typography>
          </Stack>
          <Button
            component="a"
            href="mailto:sales@layerd.com?subject=MCP%20Security%20upgrade%20request"
            startIcon={<Mail size={18} />}
            sx={{
              backgroundColor: colors.primary[300],
              color: "white",
              fontSize: 14, fontWeight: 500,
              px: 3, py: 1.25, borderRadius: "8px",
              textTransform: "none", boxShadow: "none",
              "&:hover": { backgroundColor: colors.primary[400], boxShadow: "none" },
            }}
          >
            Contact account team
          </Button>
          {featureKey ? (
            <Typography sx={{ fontSize: 12, color: colors.neutral[500], textAlign: "center", mt: 1 }}>
              Reference:{" "}
              <Box
                component="code"
                sx={{
                  color: colors.neutral[700],
                  backgroundColor: colors.neutral[100],
                  px: 0.75, py: 0.25, borderRadius: "4px",
                }}
              >
                {featureKey}
              </Box>
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

export default function McpLicenseGate({ featureKey = "mcp.endpoints", children }) {
  const { sauser } = useAuth();
  const { loaded } = useEffectiveFeaturesState();
  const allowed = useEffectiveFeature(featureKey);
  // Super admin (sauser session) has platform-level access to all modules
  // regardless of per-org SKU grants.
  if (sauser) return <>{children}</>;
  // Only spin while the effective-features map is still loading. Once
  // loaded, treat an unknown/undefined key as unlicensed — do not block
  // the user on a spinner because the CP's licensing map just doesn't
  // enumerate the key (fail-closed is the right default for a licensed
  // module).
  if (!loaded) {
    return (
      <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (allowed === true) {
    return <>{children}</>;
  }
  return <FeatureNotLicensed featureKey={featureKey} />;
}
